import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';

import { ApiParams } from '../../common/api/api';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import SearchBox from '../../common/SearchBox';
import {
  useFindUsers,
  useGetUserSummary,
  useUserFilterQueryParams
} from '../../user/User.hooks';
import { UserPermissionFilter } from '../../user/User.interface';
import UserListFilter from './components/UserListFilter';
import UserCreateDialog from './UserCreateDialog';

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  h3 {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const UserListPage: React.FC = props => {
  const filter = useUserFilterQueryParams();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    StringParam
  );

  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const history = useHistory();
  const [search, setSearch] = useState('');
  const { userList, get } = useFindUsers();
  const { userSummary, getUserSummary } = useGetUserSummary();

  useEffect(() => {
    const userFilter: UserPermissionFilter = {};
    if (filter.options?.groupIds) {
      userFilter.groupIds = filter.options.groupIds;
    }

    const params: ApiParams = {
      userFilter,
      filter: search,
      page,
      limit,
      order: String(order || '')
    };
    if (filter.options?.status) {
      params.status = filter.options.status;
    }
    if (order && orderDirection) {
      params.order = `${order} ${orderDirection}`;
    }
    get(params);
  }, [get, page, limit, order, orderDirection, search, filter.options]);

  useEffect(() => {
    getUserSummary();
  }, [getUserSummary]);

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimit(+event.target.value);
    setPage(0);
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <StyledTitle>
          <h2>Users</h2>
          {userSummary && <h3>Active: {userSummary.active}</h3>}
        </StyledTitle>
        <SearchBox value={search} onSearch={value => setSearch(value)}>
          <UserListFilter
            options={filter.options}
            onFilter={options => filter.update(options)}
          />
        </SearchBox>
        <PageTemplateHeaderActions>
          <Button
            variant='contained'
            color='primary'
            onClick={() => setIsOpenDialog(true)}
          >
            Add User
          </Button>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'id'}
                    direction={getSortDirection('id')}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'firstName'}
                    direction={getSortDirection('firstName')}
                    onClick={() => handleSort('firstName')}
                  >
                    First Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'lastName'}
                    direction={getSortDirection('lastName')}
                    onClick={() => handleSort('lastName')}
                  >
                    Last Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'email'}
                    direction={getSortDirection('email')}
                    onClick={() => handleSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'deleted'}
                    direction={getSortDirection('deleted')}
                    onClick={() => handleSort('deleted')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Teams</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {userList &&
                userList.data.map(user => (
                  <TableRow
                    hover
                    key={user.id}
                    onClick={() => history.push(`/portal/users/${user.id}`)}
                  >
                    <TableCell>USER-{user.id}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.deleted ? 'Inactive' : 'Active'}
                    </TableCell>
                    <TableCell>
                      {user.groups && user.groups.map(t => t.name).join(', ')}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={userList?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowPerPage}
        />
      </PageTemplateCardOver>

      {isOpenDialog && (
        <UserCreateDialog
          open={isOpenDialog}
          onCancel={() => setIsOpenDialog(false)}
          onCreated={() => {
            setIsOpenDialog(false);
            get(); // refresh data
          }}
        />
      )}
    </PageTemplate>
  );
};

export default UserListPage;
